/**
 * As solicitações de acesso, e a decisão sobre elas.
 *
 * ## Por que esta tela existe
 *
 * Ninguém da equipe tem acesso à tela do ServiceNow. Sem esta lista, um pedido
 * parado esperando aprovação é **invisível**: quem pediu vê "em andamento" na
 * tela dele, e do lado de cá não há onde olhar. Era aceitável enquanto a
 * simulação aprovava tudo sozinha; deixou de ser no dia em que ela foi
 * desligada e a aprovação passou a depender de uma pessoa.
 *
 * ## O que o botão Aprovar faz, e o que ele não faz
 *
 * Ele **não libera o acesso aqui**. Ele registra a aprovação no ServiceNow, e
 * quem concede continua sendo o acompanhamento do worker, na passada seguinte —
 * o mesmo código que trata a aprovação feita por uma pessoa dentro do
 * ServiceNow. Por isso a mensagem fala em "até 1 minuto" em vez de dizer
 * "liberado": prometer no clique seria mentir por esse intervalo.
 *
 * O motivo de ser assim está no backend, em `servicenow/requests.js`. Em
 * resumo: um só caminho para conceder, e nenhum chamado abandonado em aberto na
 * instância deles.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, SessionError, goToSignIn } from '../api.js';
import { notify } from './Toast.jsx';

/** Situação → como aparece. */
const STATES = {
  QUEUED: { label: 'Na fila', cls: 'info' },
  SUBMITTED: { label: 'Aguardando aprovação', cls: 'pending' },
  APPROVED: { label: 'Aprovada', cls: 'ok' },
  REJECTED: { label: 'Recusada', cls: 'neutral' },
  FAILED: { label: 'Falhou', cls: 'error' },
  CANCELLED: { label: 'Cancelada', cls: 'neutral' },
};

const FILTROS = [
  { id: '', label: 'Todas' },
  { id: 'SUBMITTED', label: 'Aguardando' },
  { id: 'QUEUED', label: 'Na fila' },
  { id: 'FAILED', label: 'Falharam' },
  { id: 'APPROVED', label: 'Aprovadas' },
  { id: 'REJECTED', label: 'Recusadas' },
];

function dateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function AdminRequests({ me }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [filtro, setFiltro] = useState('');

  /** Qual linha está com o campo de motivo aberto, e o que foi escrito nele. */
  const [recusando, setRecusando] = useState(null);
  const [motivo, setMotivo] = useState('');

  /**
   * As decididas neste minuto, por `request_id`.
   *
   * Existe porque a linha **não muda de estado na hora**: a decisão vai para o
   * ServiceNow e quem concede é o worker, na passada seguinte. Sem esta marca,
   * a tela fica idêntica depois do clique — e em 14/10 foi exatamente isso que
   * levou a clicar três vezes seguidas, recebendo `400` nas duas últimas
   * porque a aprovação já tinha sido escrita na primeira.
   */
  const [decididas, setDecididas] = useState({});

  /** `silent` para a lista não piscar "Carregando…" a cada dez segundos. */
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      setData(await api.admin.requests());
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      setError(err.message);
    } finally {
      if (!silent) setLoading(false);
    }
    return undefined;
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Memorizado por causa do `?? []`: sem isso o array literal é novo a cada
  // render e derruba o `useMemo` de baixo junto.
  const todas = useMemo(() => data?.requests ?? [], [data]);
  const visible = useMemo(
    () => (filtro ? todas.filter((r) => r.status === filtro) : todas),
    [todas, filtro],
  );

  const aguardando = todas.filter((r) => r.status === 'SUBMITTED').length;
  const naFila = todas.filter((r) => r.status === 'QUEUED').length;
  const falharam = todas.filter((r) => r.status === 'FAILED').length;

  /**
   * Acompanha enquanto houver o que mudar.
   *
   * A tela promete "o acesso é liberado na próxima verificação, em até 1
   * minuto" — e sem isto ela não cumpria a promessa: a linha ficava parada até
   * alguém clicar em Atualizar. A tela que faz a ação era justamente a que não
   * mostrava o resultado.
   *
   * Mesmo desenho da lista de solicitações da pessoa: dez segundos, pausa com
   * a aba escondida, e **para quando não há mais nada em aberto** — consultar
   * para sempre uma tela que não muda é desperdício.
   *
   * `decididas` entra na conta porque uma linha recém-decidida ainda chega
   * como `SUBMITTED` do servidor: o worker só vai fechá-la na passada
   * seguinte, e é exatamente essa transição que se quer ver acontecer.
   */
  const emMovimento = aguardando + naFila + Object.keys(decididas).length;

  useEffect(() => {
    if (emMovimento === 0) return undefined;
    const timer = setInterval(() => {
      if (!document.hidden) load({ silent: true });
    }, 10_000);
    return () => clearInterval(timer);
  }, [emMovimento, load]);

  // Solta a marca local assim que o servidor conta a mesma história. Sem isto
  // a linha ficaria dizendo "liberando…" para sempre, mesmo já fechada.
  useEffect(() => {
    setDecididas((d) => {
      const abertas = new Set(todas.filter((r) => r.status === 'SUBMITTED').map((r) => r.id));
      const restantes = Object.fromEntries(Object.entries(d).filter(([id]) => abertas.has(id)));
      return Object.keys(restantes).length === Object.keys(d).length ? d : restantes;
    });
  }, [todas]);

  async function decidir(pedido, decision, comment) {
    setBusy(pedido.id);
    try {
      const r = await api.admin.decideRequest(pedido.id, decision, comment);
      // Marca ANTES de recarregar: o `load()` traz a linha ainda como
      // `SUBMITTED`, porque o worker só vai fechá-la na passada seguinte.
      setDecididas((d) => ({ ...d, [pedido.id]: decision }));
      notify(r.message ?? 'Decisão registrada.');
      setRecusando(null);
      setMotivo('');
      await load();
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      notify(err.message);
    } finally {
      setBusy(null);
    }
    return undefined;
  }

  return (
    <>
      <div className="stats-bar">
        <div className="stat">
          <div className="stat-label">Aguardando aprovação</div>
          <div className="stat-value">{data ? aguardando : '—'}</div>
        </div>
        <div className="stat">
          <div className="stat-label">Na fila</div>
          <div className="stat-value">{data ? naFila : '—'}</div>
        </div>
        <div className="stat">
          <div className="stat-label">Falharam</div>
          <div className="stat-value">{data ? falharam : '—'}</div>
        </div>
      </div>

      <div className="list-panel">
        <div className="tabs-row">
          <div className="tabs" role="tablist" aria-label="Filtrar solicitações">
            {FILTROS.map((f) => (
              <button
                key={f.id || 'todas'}
                type="button"
                role="tab"
                aria-selected={filtro === f.id}
                className={`tab ${filtro === f.id ? 'is-active' : ''}`}
                onClick={() => setFiltro(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="tabs-actions">
            {/* Mesma classe da lista da pessoa: o estilo já existe, e duas
                marcações diferentes para o mesmo sinal divergem na primeira
                mudança de tema. */}
            {emMovimento > 0 && (
              <span className="live-hint" role="status">
                Acompanhando em tempo real…
              </span>
            )}
            <button
              type="button"
              className="button ghost"
              onClick={() => load()}
              disabled={loading}
            >
              Atualizar
            </button>
          </div>
        </div>

        {loading && <p className="state-message">Carregando…</p>}

        {error && (
          <div className="state-message error" role="alert">
            <p>{error}</p>
            <button type="button" onClick={load}>
              Tentar de novo
            </button>
          </div>
        )}

        {!loading && !error && visible.length === 0 && (
          <p className="state-message">Nenhuma solicitação com esse filtro.</p>
        )}

        {!loading && !error && visible.length > 0 && (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Quem pediu</th>
                  <th scope="col">Item</th>
                  <th scope="col">Chamado</th>
                  <th scope="col">Situação</th>
                  <th scope="col">Quando</th>
                  <th scope="col" className="col-action">
                    <span className="visually-hidden">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => {
                  const s = STATES[r.status] ?? { label: r.status, cls: 'neutral' };
                  const trabalhando = busy === r.id;
                  // Já decidida nesta sessão: a linha ainda vem `SUBMITTED` do
                  // servidor, mas a decisão já foi escrita no ServiceNow.
                  const recemDecidida = decididas[r.id];
                  // A mesma regra do servidor, espelhada: ninguém decide a
                  // própria solicitação. Mostrar o botão aqui seria oferecer
                  // uma ação que sempre responde 400.
                  const minha = Boolean(me?.oid) && r.userOid === me.oid;
                  // Só o que está esperando aprovação e tem chamado de verdade
                  // pode ser decidido. Sem `snSysId` não há onde escrever.
                  const decidivel =
                    r.status === 'SUBMITTED' && Boolean(r.snSysId) && !minha && !recemDecidida;

                  return (
                    <tr key={r.id}>
                      <th scope="row" className="cell-person">
                        <span className="person-text">
                          <span className="person-name">{r.userName ?? '—'}</span>
                          <span className="person-upn">{r.userUpn}</span>
                        </span>
                      </th>

                      <td>
                        <span className="cell-main">{r.itemName ?? r.itemId}</span>
                        <span className="cell-sub">
                          {r.itemKind === 'AGENT' ? 'Agente' : 'Sistema'}
                        </span>
                      </td>

                      <td>
                        <span className="cell-main">{r.ticketNumber ?? '—'}</span>
                        {r.lastError && <span className="cell-sub">{r.lastError}</span>}
                      </td>

                      <td>
                        {recemDecidida ? (
                          <>
                            <span className="status-pill ok">
                              {recemDecidida === 'APPROVED' ? 'Aprovada' : 'Recusada'}
                            </span>
                            <span className="cell-sub">liberando… em até 1 minuto</span>
                          </>
                        ) : (
                          <span className={`status-pill ${s.cls}`}>{s.label}</span>
                        )}
                        {r.justification && <span className="cell-sub">“{r.justification}”</span>}
                      </td>

                      <td>
                        <span className="cell-main">{dateTime(r.createdAt)}</span>
                      </td>

                      <td className="col-action">
                        {minha && r.status === 'SUBMITTED' && (
                          <span className="cell-sub">você pediu — outro administrador decide</span>
                        )}

                        {decidivel && recusando !== r.id && (
                          <>
                            <button
                              type="button"
                              className="button ghost row-action"
                              disabled={trabalhando}
                              onClick={() => decidir(r, 'APPROVED')}
                            >
                              {trabalhando ? '…' : 'Aprovar'}
                            </button>
                            <button
                              type="button"
                              className="button ghost row-action"
                              disabled={trabalhando}
                              onClick={() => {
                                setRecusando(r.id);
                                setMotivo('');
                              }}
                            >
                              Recusar
                            </button>
                          </>
                        )}

                        {decidivel && recusando === r.id && (
                          <div className="field">
                            <label className="field-label" htmlFor={`motivo-${r.id}`}>
                              Motivo da recusa
                            </label>
                            <input
                              id={`motivo-${r.id}`}
                              className="input"
                              value={motivo}
                              maxLength={500}
                              placeholder="Vai para o chamado e para quem pediu"
                              onChange={(e) => setMotivo(e.target.value)}
                            />
                            <button
                              type="button"
                              className="button ghost row-action"
                              disabled={trabalhando}
                              onClick={() => decidir(r, 'REJECTED', motivo.trim() || undefined)}
                            >
                              {trabalhando ? '…' : 'Confirmar recusa'}
                            </button>
                            <button
                              type="button"
                              className="button ghost row-action"
                              disabled={trabalhando}
                              onClick={() => setRecusando(null)}
                            >
                              Cancelar
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="panel-ft">
          Aprovar ou recusar aqui <b>escreve a decisão no ServiceNow</b>, no mesmo lugar em que uma
          pessoa decidiria por lá. O acesso em si é liberado pelo acompanhamento do worker na
          verificação seguinte, em até um minuto — por isso a linha não muda para “Aprovada” no
          instante do clique. Ninguém pode decidir a própria solicitação.
          {data?.simulation && (
            <>
              {' '}
              <b>A simulação está ligada:</b> toda solicitação é aprovada sozinha em 30 segundos,
              sem aprovador e sem chamado. Enquanto estiver assim, esta lista não reflete decisão de
              ninguém.
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default AdminRequests;
